import * as React from 'react'

import { DocumentNode } from '@apollo/client'
import { F0Button } from '@factorialco/f0-react'
import { OneModal, Textarea } from '@factorialco/f0-react/dist/experimental'
import {
  RequestChangesPurchaseRequestReviewRequestDocument,
  PurchaseRequestDetailDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface RequestChangesModalProps {
  isOpen: boolean
  onClose: () => void
  reviewRequestIds: number[]
  onSuccess?: () => void
  refetchQueries?: DocumentNode[]
}

const RequestChangesModal: React.FC<RequestChangesModalProps> = ({
  isOpen,
  onClose,
  reviewRequestIds,
  onSuccess,
  refetchQueries = [PurchaseRequestDetailDocument],
}) => {
  const { company, employee } = useCurrentSession()

  const [bulkRequestChangesReviewRequest, { loading }] = useMutation(
    RequestChangesPurchaseRequestReviewRequestDocument,
    {
      extractorFn: (data) =>
        data.approvalsV2Mutations.bulkRequestChangesApprovalsv2ReviewRequest,
      refetchQueries,
    }
  )

  const [reason, setReason] = React.useState('')

  const handleClose = () => {
    setReason('')
    onClose()
  }

  const handleSubmit = async () => {
    if (reviewRequestIds.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_requests.detail.responses.changes_requested.no_review_requests'),
      })
      return
    }

    const result = await bulkRequestChangesReviewRequest({
      variables: {
        companyId: company.id,
        reviewRequestIds: reviewRequestIds,
        reviewerId: employee.id,
        reason: reason || undefined,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_requests.detail.responses.changes_requested.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.purchase_requests.detail.responses.changes_requested.success'),
      })

      handleClose()
      onSuccess?.()
    }
  }

  return (
    <OneModal isOpen={isOpen} onClose={handleClose}>
      <OneModal.Header title={i18n.t('procurement.purchase_requests.detail.changes_requested_modal.title')} />
      <OneModal.Content withPadding>
        <Textarea
          label={i18n.t('procurement.purchase_requests.detail.changes_requested_modal.placeholder')}
          hideLabel
          placeholder={i18n.t('procurement.purchase_requests.detail.changes_requested_modal.placeholder')}
          value={reason}
          onChange={setReason}
        />
      </OneModal.Content>
      <OneModal.Footer>
        <F0Button
          label={i18n.t('common.cancel')}
          variant="secondary"
          onClick={handleClose}
          disabled={loading}
        />
        <F0Button
          label={i18n.t('procurement.purchase_requests.detail.actions.changes_requested')}
          loading={loading}
          onClick={handleSubmit}
          disabled={loading}
        />
      </OneModal.Footer>
    </OneModal>
  )
}

export default RequestChangesModal
